import React, { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { departmentAPI } from '../../services/api';
import { formatMarathiNumber } from '../../utils/marathi';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import './DepartmentsPage.css';

const META = {
  health: { label: 'आरोग्य विभाग', icon: 'heart', color: '#00a786', bg: '#e7fbf5' },
  agriculture: { label: 'कृषी विभाग', icon: 'tractor', color: '#76a82a', bg: '#eff9e6' },
  water_and_sanitation: { label: 'पाणीपुरवठा विभाग', icon: 'drop', color: '#1c92ed', bg: '#eaf6ff' },
  pwd: { label: 'सार्वजनिक बांधकाम विभाग', icon: 'road', color: '#d97736', bg: '#fff1e8' },
  education: { label: 'शिक्षण विभाग', icon: 'cap', color: '#8e44ad', bg: '#f5eafe' },
  women_child: { label: 'महिला व बाल विकास विभाग', icon: 'family', color: '#e43f73', bg: '#fff0f5' },
  social_welfare: { label: 'सामाजिक न्याय विभाग', icon: 'group', color: '#259b51', bg: '#eaf8ef' },
  finance: { label: 'वित्त व लेखा विभाग', icon: 'coins', color: '#9b59b6', bg: '#f4eafd' },
  administration: { label: 'प्रशासकीय विभाग', icon: 'building', color: '#2563eb', bg: '#edf4ff' },
  animal_husbandry: { label: 'पशुसंवर्धन विभाग', icon: 'heart', color: '#0f9f8f', bg: '#e6fbf7' },
  rural_development: { label: 'ग्रामविकास विभाग', icon: 'home', color: '#f59e0b', bg: '#fff6de' },
  tourism: { label: 'पर्यटन विभाग', icon: 'map', color: '#0ea5e9', bg: '#e8f8ff' },
  other: { label: 'इतर विभाग', icon: 'grid', color: '#64748b', bg: '#eef2f6' },
};

function Icon({ name, size = 30 }) {
  const paths = {
    heart: <><path d="M20.8 4.6a5.4 5.4 0 0 0-7.6 0L12 5.8l-1.2-1.2a5.4 5.4 0 1 0-7.6 7.6L12 21l8.8-8.8a5.4 5.4 0 0 0 0-7.6Z" /><path d="M7 12h3l1-3 2 6 1-3h3" /></>,
    tractor: <><path d="M7 14V6h7l3 5h2" /><circle cx="7" cy="17" r="3" /><circle cx="18" cy="17" r="2" /><path d="M10 17h6M10 9h5" /></>,
    drop: <path d="M12 3s7 7.1 7 12a7 7 0 0 1-14 0c0-4.9 7-12 7-12Z" />,
    road: <><path d="M7 21 11 3h2l4 18" /><path d="M12 7v2M12 13v2M5 21h14" /></>,
    cap: <><path d="m3 8 9-5 9 5-9 5-9-5Z" /><path d="M7 10v5c3 2 7 2 10 0v-5" /></>,
    family: <><circle cx="9" cy="7" r="3" /><circle cx="17" cy="8" r="2.5" /><path d="M3 21a6 6 0 0 1 12 0M14 21a5 5 0 0 1 7 0" /></>,
    group: <><circle cx="8" cy="8" r="3" /><circle cx="16" cy="8" r="3" /><path d="M2 21a6 6 0 0 1 12 0M10 21a6 6 0 0 1 12 0" /></>,
    coins: <><ellipse cx="12" cy="6" rx="7" ry="3" /><path d="M5 6v6c0 1.7 3.1 3 7 3s7-1.3 7-3V6M5 12v6c0 1.7 3.1 3 7 3s7-1.3 7-3v-6" /></>,
    building: <><path d="M4 21V7l8-4 8 4v14" /><path d="M9 21v-6h6v6M8 9h.01M12 9h.01M16 9h.01M8 13h.01M16 13h.01" /></>,
    home: <><path d="m3 11 9-8 9 8" /><path d="M5 10v11h14V10M9 21v-6h6v6" /></>,
    map: <><path d="m9 18-6 3V6l6-3 6 3 6-3v15l-6 3-6-3Z" /><path d="M9 3v15M15 6v15" /></>,
    grid: <><rect x="4" y="4" width="6" height="6" rx="1" /><rect x="14" y="4" width="6" height="6" rx="1" /><rect x="4" y="14" width="6" height="6" rx="1" /><rect x="14" y="14" width="6" height="6" rx="1" /></>,
    search: <><circle cx="11" cy="11" r="7" /><path d="m20 20-4-4" /></>,
    arrow: <path d="M5 12h14M13 5l7 7-7 7" />,
    phone: <><path d="M6.5 3h3l1.5 5-2 1.5a15 15 0 0 0 5.5 5.5l1.5-2 5 1.5v3A3.5 3.5 0 0 1 17.5 21C9.5 21 3 14.5 3 6.5A3.5 3.5 0 0 1 6.5 3Z" /></>,
  };
  return <svg viewBox="0 0 24 24" width={size} height={size} fill="none" stroke="currentColor" strokeWidth="1.85" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">{paths[name] || paths.grid}</svg>;
}

const metaFor = type => META[type] || META.other;
const getTitle = dept => dept?.name || dept?.name_english || 'विभाग';
const splitList = value => Array.isArray(value) ? value.filter(Boolean) : String(value || '').split(/\r?\n|,/).map(item => item.trim()).filter(Boolean);

export default function DepartmentsPage() {
  const { tenantSlug } = useParams();
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [type, setType] = useState('all');

  useEffect(() => {
    setLoading(true);
    departmentAPI.getAll(tenantSlug)
      .then(res => setDepartments(Array.isArray(res.data) ? res.data : []))
      .catch(() => setError('विभागांची माहिती लोड करता आली नाही.'))
      .finally(() => setLoading(false));
  }, [tenantSlug]);

  const types = useMemo(() => [...new Set(departments.map(dept => dept.department_type).filter(Boolean))], [departments]);
  const filtered = useMemo(() => departments.filter(dept => {
    const query = search.trim().toLowerCase();
    const text = [dept.name, dept.name_english, dept.description, dept.head_name, dept.department_type].filter(Boolean).join(' ').toLowerCase();
    return (!query || text.includes(query)) && (type === 'all' || dept.department_type === type);
  }), [departments, search, type]);

  const staffCount = departments.reduce((sum, dept) => sum + (dept.StaffMembers?.length || 0), 0);
  const servicesCount = departments.reduce((sum, dept) => sum + splitList(dept.services_offered).length, 0);

  return (
    <div className="departments-page">
      <section className="dept-list-hero">
        <div>
          <nav><Link to={`/gp/${tenantSlug}`}>मुख्यपृष्ठ</Link><span>›</span><strong>विभाग</strong></nav>
          <h1>विभाग</h1>
          <p>ग्रामपंचायत अंतर्गत कार्यरत विविध विभागांची माहिती, सेवा, अधिकारी आणि संपर्क तपशील.</p>
        </div>
      </section>

      <section className="dept-list-stats">
        <article><span><Icon name="grid" /></span><strong>{formatMarathiNumber(departments.length)}</strong><p>एकूण विभाग</p></article>
        <article><span><Icon name="group" /></span><strong>{formatMarathiNumber(staffCount)}</strong><p>कर्मचारी / अधिकारी</p></article>
        <article><span><Icon name="building" /></span><strong>{formatMarathiNumber(departments.filter(d => d.is_active !== false).length)}</strong><p>सक्रिय विभाग</p></article>
        <article><span><Icon name="heart" /></span><strong>{formatMarathiNumber(servicesCount)}</strong><p>नोंदवलेल्या सेवा</p></article>
      </section>

      <section className="dept-toolbar">
        <div className="dept-search">
          <Icon name="search" size={18} />
          <input value={search} onChange={event => setSearch(event.target.value)} placeholder="विभाग, सेवा किंवा अधिकारी शोधा..." />
        </div>
        <select value={type} onChange={event => setType(event.target.value)}>
          <option value="all">सर्व विभाग</option>
          {types.map(item => <option key={item} value={item}>{metaFor(item).label}</option>)}
        </select>
      </section>

      {loading ? (
        <LoadingSpinner fullPage message="विभाग लोड होत आहेत..." />
      ) : error ? (
        <div className="dept-state error">{error}</div>
      ) : filtered.length === 0 ? (
        <div className="dept-state">या शोधासाठी विभाग उपलब्ध नाहीत.</div>
      ) : (
        <section className="dept-grid">
          {filtered.map(dept => {
            const meta = metaFor(dept.department_type);
            const services = splitList(dept.services_offered);
            return (
              <article className="dept-card" key={dept.id} style={{ '--dept-color': meta.color, '--dept-bg': meta.bg }}>
                <div className="dept-card-icon">{dept.icon ? <img src={dept.icon} alt="" /> : <Icon name={meta.icon} size={38} />}</div>
                <h2>{getTitle(dept)}</h2>
                <p>{dept.description || 'या विभागाची माहिती लवकरच उपलब्ध होईल.'}</p>
                <div className="dept-card-meta">
                  <span>{formatMarathiNumber(services.length || 0)} सेवा</span>
                  <span>{formatMarathiNumber(dept.StaffMembers?.length || 0)} कर्मचारी</span>
                </div>
                {dept.head_name && <small>प्रमुख: {dept.head_name}</small>}
                <Link to={`/gp/${tenantSlug}/departments/${dept.id}`} className="dept-details-link">
                  तपशील पहा <Icon name="arrow" size={15} />
                </Link>
              </article>
            );
          })}
        </section>
      )}
    </div>
  );
}
